import { useState, useCallback } from 'react';
import SectionLabel from '../common/SectionLabel';
import { useFinancialData, ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';
import { usePipeline } from '../../context/PipelineContext';
import { apiFetch } from '../../utils/apiFetch';

const SEVERITY_CONFIG = {
  red: {
    dot: 'bg-rose',
    bg: 'bg-rose/5',
    border: 'border-rose/20',
    label: 'ATTENTION',
    labelBg: 'bg-rose/10 text-rose',
  },
  yellow: {
    dot: 'bg-amber-400',
    bg: 'bg-amber-50',
    border: 'border-amber-200/50',
    label: 'MONITOR',
    labelBg: 'bg-amber-100 text-amber-700',
  },
  green: {
    dot: 'bg-deep-teal',
    bg: 'bg-deep-teal/5',
    border: 'border-deep-teal/15',
    label: 'ON TRACK',
    labelBg: 'bg-deep-teal/10 text-deep-teal',
  },
};

// Sort: red first, then yellow, then green
const SEVERITY_ORDER = { red: 0, yellow: 1, green: 2 };

const LS_KEY = 'bcc_ai_watchlist';

function loadCached() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return null;
}

export default function AIWatchlist({ cashFlowRows }) {
  const { annualSummary, targets, kpi, global2026Monthly, editableByClass } = useFinancialData();
  const { pipelineKPIs, stageSummary, prospects, conversionRates } = usePipeline();

  const cached = loadCached();
  const [observations, setObservations] = useState(cached?.observations || null);
  const [lastUpdated, setLastUpdated] = useState(cached?.timestamp || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [activeFilters, setActiveFilters] = useState({ red: true, yellow: true, green: true });

  const toggleExpand = useCallback((idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const toggleFilter = useCallback((severity) => {
    setActiveFilters((prev) => ({ ...prev, [severity]: !prev[severity] }));
  }, []);

  const filteredObservations = observations
    ? observations.filter((o) => activeFilters[o.severity])
    : null;

  const allExpanded = filteredObservations?.length > 0 &&
    filteredObservations.every((_, idx) => {
      const origIdx = observations.indexOf(filteredObservations[idx]);
      return expanded[origIdx];
    });

  const expandCollapseAll = useCallback(() => {
    if (!observations) return;
    if (allExpanded) {
      setExpanded({});
    } else {
      const all = {};
      observations.forEach((o, idx) => {
        if (activeFilters[o.severity]) all[idx] = true;
      });
      setExpanded(all);
    }
  }, [observations, allExpanded, activeFilters]);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build the financial data payload
      const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

      // Summarize current year P&L by month
      const currentYear = {};
      if (global2026Monthly?.months) {
        for (const mk of monthKeys) {
          const m = global2026Monthly.months[mk];
          if (!m) continue;
          const income = Object.values(m.income || {}).reduce((s, v) => s + (v || 0), 0);
          const cogs = Object.values(m.cogs || {}).reduce((s, v) => s + (v || 0), 0);
          const expenses = Object.values(m.expenses || {}).reduce((s, v) => s + (v || 0), 0);
          currentYear[mk] = {
            income,
            cogs,
            grossProfit: income - cogs,
            expenses,
            netIncome: income - cogs - expenses,
            isActual: monthKeys.indexOf(mk) < ACTUALS_CUTOFF_2026,
            detail: {
              income: m.income,
              cogs: m.cogs,
              topExpenses: Object.entries(m.expenses || {})
                .sort(([, a], [, b]) => b - a)
                .slice(0, 8)
                .reduce((obj, [k, v]) => { obj[k] = v; return obj; }, {}),
            },
          };
        }
      }

      // Division-level summary (annualized from current data)
      const divisionSummary = {};
      for (const cls of ['bwm', 'bts', 'bia']) {
        const classData = editableByClass?.[cls];
        if (!classData?.months) continue;
        let totalIncome = 0, totalCogs = 0, totalExpenses = 0;
        for (const mk of monthKeys) {
          const m = classData.months[mk];
          if (!m) continue;
          totalIncome += Object.values(m.income || {}).reduce((s, v) => s + (v || 0), 0);
          totalCogs += Object.values(m.cogs || {}).reduce((s, v) => s + (v || 0), 0);
          totalExpenses += Object.values(m.expenses || {}).reduce((s, v) => s + (v || 0), 0);
        }
        divisionSummary[cls] = { revenue: totalIncome, cogs: totalCogs, expenses: totalExpenses, netIncome: totalIncome - totalCogs - totalExpenses };
      }

      // Cash flow summary
      let cashFlowSummary = null;
      if (cashFlowRows?.length) {
        cashFlowSummary = cashFlowRows.map((r) => ({
          month: r.label,
          type: r.actual ? 'actual' : 'projected',
          endingBalance: r.endingBalance,
          operatingCF: r.operatingCF,
          signal: r.signal,
        }));
      }

      // Pipeline summary
      let pipelineSummary = null;
      if (prospects?.length > 0) {
        const active = prospects.filter((p) => p.outcome === null);
        pipelineSummary = {
          totalProspects: active.length,
          weightedValue: pipelineKPIs?.weightedPipelineValue || 0,
          totalValue: pipelineKPIs?.totalPipelineValue || 0,
          winRate: pipelineKPIs?.winRate || 0,
          avgDaysInPipeline: pipelineKPIs?.avgDaysInPipeline || 0,
          byStage: stageSummary,
          conversionRates,
        };
      }

      const payload = {
        financialData: {
          currentYear,
          annualSummary: annualSummary?.slice(-5),
          kpi,
          divisionSummary,
        },
        pipelineData: pipelineSummary,
        cashFlowData: cashFlowSummary,
      };

      const res = await apiFetch('/api/claude/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.success) {
        const detail = data.details ? ` -- ${data.details}` : '';
        const raw = data.raw ? ` | Raw: ${data.raw.substring(0, 200)}` : '';
        throw new Error((data.error || 'Analysis failed') + detail + raw);
      }

      // Sort by severity
      const sorted = (data.observations || []).sort(
        (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
      );

      setObservations(sorted);
      setLastUpdated(data.timestamp);
      setExpanded({});

      // Cache in localStorage
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({
          observations: sorted,
          timestamp: data.timestamp,
        }));
      } catch (e) { /* ignore */ }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [global2026Monthly, annualSummary, kpi, editableByClass, prospects, pipelineKPIs, stageSummary, conversionRates, cashFlowRows]);

  const severityCounts = observations
    ? { red: 0, yellow: 0, green: 0, ...observations.reduce((acc, o) => { acc[o.severity] = (acc[o.severity] || 0) + 1; return acc; }, {}) }
    : null;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)]">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <SectionLabel>Key Signals</SectionLabel>
          {severityCounts && (
            <div className="flex items-center gap-1.5">
              {severityCounts.red > 0 && (
                <button
                  onClick={() => toggleFilter('red')}
                  className={`px-1.5 py-0.5 text-[10px] font-bold rounded-[3px] transition-colors cursor-pointer ${
                    activeFilters.red
                      ? 'bg-rose/10 text-rose'
                      : 'bg-gray-100 text-gray-400 line-through'
                  }`}
                >
                  {severityCounts.red} alert{severityCounts.red > 1 ? 's' : ''}
                </button>
              )}
              {severityCounts.yellow > 0 && (
                <button
                  onClick={() => toggleFilter('yellow')}
                  className={`px-1.5 py-0.5 text-[10px] font-bold rounded-[3px] transition-colors cursor-pointer ${
                    activeFilters.yellow
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-400 line-through'
                  }`}
                >
                  {severityCounts.yellow} monitor
                </button>
              )}
              {severityCounts.green > 0 && (
                <button
                  onClick={() => toggleFilter('green')}
                  className={`px-1.5 py-0.5 text-[10px] font-bold rounded-[3px] transition-colors cursor-pointer ${
                    activeFilters.green
                      ? 'bg-deep-teal/10 text-deep-teal'
                      : 'bg-gray-100 text-gray-400 line-through'
                  }`}
                >
                  {severityCounts.green} on track
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-[10px] text-[#999]">
              Updated {new Date(lastUpdated).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </span>
          )}
          {observations && filteredObservations?.length > 0 && (
            <button
              onClick={expandCollapseAll}
              className="px-2.5 py-1.5 text-[11px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200 transition-colors"
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          )}
          <button
            onClick={runAnalysis}
            disabled={loading}
            className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
              loading
                ? 'bg-gray-100 text-gray-400 cursor-wait'
                : 'bg-navy text-white hover:bg-navy/90'
            }`}
          >
            {loading ? (
              <span className="flex items-center gap-1.5">
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Analyzing...
              </span>
            ) : observations ? 'Refresh Analysis' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 pb-4">
        {error && (
          <div className="p-3 mb-3 text-[12px] bg-rose/5 border border-rose/20 rounded-[4px] text-rose">
            <span className="font-bold">Error: </span>{error}
          </div>
        )}

        {!observations && !loading && !error && (
          <div className="py-8 text-center">
            <div className="text-[32px] mb-2 opacity-30">&#x1f50d;</div>
            <p className="text-[13px] text-[#888] mb-1">
              Click <span className="font-bold text-navy">Run Analysis</span> to get AI-powered insights
            </p>
            <p className="text-[11px] text-[#bbb]">
              Analyzes your P&L, cash flow, pipeline, and benchmarks
            </p>
          </div>
        )}

        {loading && !observations && (
          <div className="py-8 text-center">
            <svg className="animate-spin h-6 w-6 mx-auto mb-3 text-navy" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-[13px] text-[#888]">Analyzing financial data...</p>
            <p className="text-[11px] text-[#bbb] mt-1">This takes a few seconds</p>
          </div>
        )}

        {observations && filteredObservations?.length === 0 && (
          <div className="py-6 text-center text-[12px] text-[#999]">
            All signals filtered out. Click the severity badges above to show them.
          </div>
        )}

        {filteredObservations && filteredObservations.length > 0 && (
          <div className="space-y-2">
            {filteredObservations.map((obs) => {
              const origIdx = observations.indexOf(obs);
              const config = SEVERITY_CONFIG[obs.severity] || SEVERITY_CONFIG.yellow;
              const isExpanded = expanded[origIdx];

              return (
                <div
                  key={origIdx}
                  onClick={() => toggleExpand(origIdx)}
                  className={`border rounded-[4px] cursor-pointer transition-colors ${config.border} ${
                    isExpanded ? config.bg : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3 px-4 py-3">
                    {/* Severity dot */}
                    <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${config.dot}`} />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[13px] font-bold text-navy">
                          {obs.title || obs.observation?.substring(0, 50)}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-[3px] uppercase tracking-wider ${config.labelBg}`}>
                          {config.label}
                        </span>
                      </div>
                      <p className={`text-[12px] text-[#666] leading-relaxed ${isExpanded ? '' : 'line-clamp-1'}`}>
                        {obs.observation}
                      </p>
                      {isExpanded && obs.recommendation && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-deep-teal mb-1">Recommendation</p>
                          <p className="text-[12px] text-[#555] leading-relaxed">{obs.recommendation}</p>
                        </div>
                      )}
                    </div>

                    {/* Expand indicator */}
                    <svg
                      className={`w-4 h-4 text-[#ccc] flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
